/**
 * Handles "Failed to fetch dynamically imported module" errors that occur when
 * a user has a stale page loaded after a new deployment. Forces a full page
 * reload so the browser fetches fresh HTML and the correct new chunk URLs.
 * A sessionStorage guard prevents infinite reload loops if the error persists.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  // Each deploy has a new buildId, so a stale tab gets one reload onto the new
  // build, and that build gets one more try if it hits the error too.
  const buildId = useRuntimeConfig().app?.buildId ?? ''

  function handleChunkError(error, { force = false } = {}) {
    const msg = String(error?.message ?? '') || String(error ?? '')
    const isChunkError = force ||
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('is not a valid JavaScript MIME type')

    if (!isChunkError) return

    // Key on build + path rather than the chunk URL: Safari's message
    // ("Importing a module script failed.") has no URL to key on.
    const guardKey = `chunk-reload:${buildId}:${window.location.pathname}`
    try {
      if (sessionStorage.getItem(guardKey)) {
        console.error('[Chunk Reload] Already reloaded, not retrying:', msg)
        return
      }
      sessionStorage.setItem(guardKey, '1')
    } catch {
      // sessionStorage may throw in private/restricted contexts — fall back to
      // a URL hash that survives the reload. Not per-build, but prevents an
      // infinite loop when the error persists. The app doesn't use hash routing.
      if (window.location.hash === '#chunk-reloaded') {
        console.error('[Chunk Reload] Already reloaded (hash), not retrying:', msg)
        return
      }
      window.location.hash = 'chunk-reloaded'
    }

    console.log('[Chunk Reload] Chunk error detected, reloading...', error)
    window.location.reload()
    return true
  }

  // app:error fires for failed async route component loads in Nuxt SPA mode
  nuxtApp.hook('app:error', (error) => handleChunkError(error))

  // router.onError as a fallback for errors that surface through Vue Router
  router.onError((error) => handleChunkError(error))

  // Vite fires this when a dynamic import's preload fails, whatever the
  // browser's message. preventDefault stops Vite from rethrowing it, so only do
  // that when reloading; otherwise callers' own error fallbacks need the error.
  window.addEventListener('vite:preloadError', (event) => {
    if (handleChunkError(event.payload, { force: true })) event.preventDefault()
  })
})
